import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useCRM } from '../context/CRMContext';
import { format } from 'date-fns';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import DepartmentForm from '../components/Forms/DepartmentForm';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';

const { FiPlus, FiEdit, FiTrash2, FiUsers, FiAlertTriangle } = FiIcons;

const Departments = () => {
  const { departments, users, addDepartment, updateDepartment, deleteDepartment } = useCRM();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  
  // Delete confirmation state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);

  const handleAddDepartment = () => {
    setEditingDepartment(null);
    setIsModalOpen(true);
  };

  const handleEditDepartment = (department) => {
    setEditingDepartment(department);
    setIsModalOpen(true);
  };

  const handleSubmit = (data) => {
    if (editingDepartment) {
      updateDepartment(editingDepartment.id, data);
    } else {
      addDepartment(data);
    }
    setIsModalOpen(false);
    setEditingDepartment(null);
  };

  const handleDeleteClick = (department) => {
    // Check if any users are assigned to this department
    // users array from context has camelCase keys (departmentId)
    const hasUsers = users.some(u => u.departmentId === department.id);
    
    if (hasUsers) {
      toast.error('Cannot delete this department because users are currently assigned to it. Please reassign them to another department first.', {
        duration: 5000,
        icon: '⚠️'
      });
      return;
    }

    setDepartmentToDelete(department);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (departmentToDelete) {
      await deleteDepartment(departmentToDelete.id);
      setDeleteModalOpen(false);
      setDepartmentToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Departments</h1>
        <Button onClick={handleAddDepartment}>
          <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
          Add Department
        </Button>
      </div>

      {departments?.length === 0 ? (
        <Card className="p-12 text-center">
          <SafeIcon icon={FiUsers} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No departments yet</h3>
          <p className="text-gray-500 mb-4">Add departments to organize your team structure.</p>
          <Button onClick={handleAddDepartment}>
            <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
            Add Department
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {departments?.map((department, index) => (
            <motion.div
              key={department.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="p-4 hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {department.name}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Added {format(new Date(department.createdAt), 'MMM dd, yyyy')}
                    </p>
                  </div>
                  <div className="flex space-x-1 ml-2">
                    <button
                      onClick={() => handleEditDepartment(department)}
                      className="text-gray-400 hover:text-primary-600 transition-colors p-1"
                      title="Edit"
                    >
                      <SafeIcon icon={FiEdit} className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(department)}
                      className="text-gray-400 hover:text-red-600 transition-colors p-1"
                      title="Delete"
                    >
                      <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Edit/Add Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingDepartment ? 'Edit Department' : 'Add New Department'}
        size="md"
      >
        <DepartmentForm
          department={editingDepartment}
          onSubmit={handleSubmit}
          onCancel={() => setIsModalOpen(false)}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Department"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-center text-red-100 bg-red-50 p-4 rounded-full w-16 h-16 mx-auto mb-4">
            <SafeIcon icon={FiAlertTriangle} className="h-8 w-8 text-red-600" />
          </div>
          
          <div className="text-center">
            <p className="text-sm text-gray-500">
              Are you sure you want to delete <strong>{departmentToDelete?.name}</strong>?
              <br/>This action cannot be undone.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete Department
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Departments;